import { useState, useMemo } from 'react';

import { pdf } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

import useEnv from '@zenbusiness/application-commons-env';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';
import { useCustomForm } from '#client/contexts/CustomFormContext';

import * as Styled from './BusinessIdeaResults.style';
import PoweredByVelo from '../PoweredByVelo/PoweredByVelo';
import { ReportPanel } from '../ReportLayout/ReportPanel';
import SelectionPanel from '../ReportLayout/SelectionPanel';
import SidePanel from '../ReportLayout/SidePanel';
import { BUSINESS_IDEA_SECTIONS } from '../utils/constants';
import { BusinessIdeaPDF } from './components/BusinessIdeaResultPDF/BusinessIdeaResultPDF';
import { BusinessIdeaResultsData } from './types';

/**
 * Business Idea Results component
 * Displays personalized business ideas and detailed reports
 * Shows market analysis, target customers, revenue models, and startup steps
 * Uses shared ReportPanel for consistent reporting UI
 *
 * @param {object} props - Component props
 * @param {object} props.data - Business results data containing ideas and user profile
 * @param {string[]} [props.actions] - Enabled actions: 'download_pdf', 'generate_more', 'start_over'
 * @param {Function} [props.onComponentClick] - Callback for component actions (follows assistant-ui pattern)
 * @component
 */
function BusinessIdeaResults({ data, actions = [], onComponentClick }) {
  const { DEPLOY_ENV } = useEnv('head-first');
  const { answers, isLoading, setLoading, resetForm, clearAnswers } = useCustomForm();

  const enabledActions = Array.isArray(actions) ? actions : [];
  // Need to add these on api side then we can remove the hard coded true
  const canDownloadPdf = true || enabledActions.includes('download_pdf');
  const canGenerateMore = true || enabledActions.includes('generate_more');
  const canStartOver = true || enabledActions.includes('start_over');

  // Support both data.ideas and data.items - memoize to prevent dependency issues
  const items = useMemo(() => data.ideas || data.items || [], [data.ideas, data.items]);

  const [selectedIdeaId, setSelectedIdeaId] = useState(data.selectedIdeaId || items[0]?.id);

  // Find the currently selected idea
  const selectedIdea = useMemo(
    () => items.find((idea) => idea.id === selectedIdeaId),
    [items, selectedIdeaId]
  );

  const handleIdeaSelect = (ideaId) => {
    setSelectedIdeaId(ideaId);
  };

  const handleDownloadReport = async () => {
    if (isLoading) return;

    setLoading(true);

    try {
      const blob = await pdf(
        <BusinessIdeaPDF environment={DEPLOY_ENV} idea={selectedIdea} />
      ).toBlob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedIdea.title.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[BusinessResults] Download report error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMore = async () => {
    if (isLoading) return;

    setLoading(true);
    if (onComponentClick) {
      await onComponentClick({
        type: 'business_results_generate_more',
        payload: {
          answers
        }
      });
    }
    setLoading(false);
  };

  const handleStartOver = async () => {
    if (isLoading) return;

    setLoading(true);
    if (onComponentClick) {
      await resetForm();
      await clearAnswers();
      await onComponentClick({ type: 'business_results_start_over' });
    }
    setLoading(false);
  };

  // Handle case where no ideas are provided
  if (!items || items.length === 0) {
    return (
      <Styled.BusinessIdeaResultsRoot>
        <Box style={{ textAlign: 'center' }}>
          <Text>No business ideas found</Text>
          <Text>
            We weren&apos;t able to generate business ideas based on your responses. Please try
            again.
          </Text>
        </Box>
      </Styled.BusinessIdeaResultsRoot>
    );
  }

  return (
    <Styled.BusinessIdeaResultsRoot>
      <Styled.IdeasHeader>
        <Text size="md" weight="medium">
          Your personalized business ideas
        </Text>
        <Text size="sm">
          Based on your responses, here are business ideas tailored specifically for you.
        </Text>
      </Styled.IdeasHeader>
      <Styled.IdeasContent>
        <SidePanel
          selectionPanel={
            <SelectionPanel
              clickstreamPrefix="business_idea"
              generateMoreText="Generate more ideas"
              getDisplayTitle={(idea) => idea.title}
              getItemId={(idea) => idea.id}
              items={items}
              loading={isLoading}
              selectedItemId={selectedIdeaId}
              startOverText="Start over"
              onGenerateMore={canGenerateMore ? handleGenerateMore : undefined}
              onItemSelect={handleIdeaSelect}
              onStartOver={canStartOver ? handleStartOver : undefined}
            />
          }
        />

        <ReportPanel
          loading={isLoading}
          reportData={selectedIdea}
          sectionsConfig={BUSINESS_IDEA_SECTIONS}
          onDownloadReport={canDownloadPdf ? handleDownloadReport : undefined}
        />
      </Styled.IdeasContent>
      <PoweredByVelo url={ROUTES.website(DEPLOY_ENV)} />
    </Styled.BusinessIdeaResultsRoot>
  );
}

BusinessIdeaResults.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.string),
  data: BusinessIdeaResultsData,
  onComponentClick: PropTypes.func
};

export default BusinessIdeaResults;
